import { createRouter, createWebHistory, type NavigationGuardNext, type RouteLocationNormalized } from 'vue-router'
import { authGuard } from '@auth0/auth0-vue'
import { useAuthStore } from '@/stores/authStore'
import { useAuth0 } from '@auth0/auth0-vue'

/**
 * requirePermission — route guard that checks the user's JWT permissions claim.
 * Uses useAuthStore.ensurePermissionsLoaded() which decodes the access-token once
 * and caches the result. Missing permission → redirect to /unauthorized.
 * Server enforces scope independently — this guard is UX-only per D-03.
 */
function requirePermission(perm: string) {
    return async (
        _to: RouteLocationNormalized,
        _from: RouteLocationNormalized,
        next: NavigationGuardNext,
    ) => {
        const auth = useAuthStore()
        const { getAccessTokenSilently } = useAuth0()
        await auth.ensurePermissionsLoaded(getAccessTokenSilently)
        if (!auth.hasPermission(perm)) {
            return next({ name: 'unauthorized' })
        }
        return next()
    }
}

export default createRouter({
    history: createWebHistory(import.meta.env.BASE_URL),
    routes: [
        {
            path: '/',
            name: 'dashboard',
            component: () => import('@/views/DashboardView.vue'),
            beforeEnter: [authGuard, requirePermission('futures:read')],
        },
        {
            path: '/contracts/:id',
            name: 'contract-editor',
            component: () => import('@/views/ContractEditorView.vue'),
            beforeEnter: [authGuard, requirePermission('futures:read')],
            props: true,
        },
        {
            path: '/contracts/:id/edit',
            name: 'contract-edit-header',
            component: () => import('@/views/ContractEditHeaderView.vue'),
            beforeEnter: [authGuard, requirePermission('futures:write')],
            props: true,
        },
        {
            path: '/positions/:contract_id',
            name: 'position-view',
            component: () => import('@/views/PositionView.vue'),
            beforeEnter: [authGuard, requirePermission('futures:read')],
            props: true,
        },
        {
            path: '/unauthorized',
            name: 'unauthorized',
            component: () => import('@/views/UnauthorizedView.vue'),
        },
        {
            path: '/:pathMatch(.*)*',
            redirect: '/',
        },
    ],
})
